import React from "react";
import { tv, VariantProps } from "tailwind-variants";
import { DropdownSearchBarProps } from "@/components/ui/dropdown-search-bar";
import Image, { StaticImageData } from "next/image";
import PrimaryButton from "@/components/ui/primary-button";
import BlogsIllustration from "@/components/illustrations/blogs-illustration";
import CIVRStoriesBannerImage from "@/assets/images/ci-vr/ci-vr-stories-banner.png";
import SecondaryButton from "@/components/ui/secondary-button";
import DropdownSearchWrapper from "@/components/ui/dropdown-search-wrapper";

const headerBlogsEventsClassesVariants = tv({
  slots: {
    root: "",
    container: "",
    leftContainer: "",
    rightContainer: "",
    contentContainer: "",
    heading: "",
    subheading: "",
    illustration: "",
    image: "",
    ctaContainer: "",
    primaryCta: "",
    secondaryCta: "",
  },
  variants: {
    type: {
      search: {
        root: "",
        container: "",
        leftContainer: "",
        rightContainer: "",
        contentContainer: "",
        heading: "",
        subheading: "",
        illustration: "",
        image: "",
        ctaContainer: "",
        primaryCta: "",
        secondaryCta: "",
      },
      "one-CTA": {
        root: "",
        container: "",
        leftContainer: "",
        rightContainer: "",
        contentContainer: "",
        heading: "",
        subheading: "",
        illustration: "",
        image: "",
        ctaContainer: "",
        primaryCta: "",
        secondaryCta: "",
      },
      "two-CTA": {
        root: "",
        container: "",
        leftContainer: "",
        rightContainer: "",
        contentContainer: "",
        heading: "",
        subheading: "",
        illustration: "",
        image: "",
        ctaContainer: "",
        primaryCta: "",
        secondaryCta: "",
      },
    },
    asset: {
      image: "",
      illustration: "",
    },
  },
  compoundVariants: [
    {
      type: "search",
      asset: "illustration",
      className: {
        root: "flex h-fit w-full flex-col bg-primary-100",
        container:
          "mx-auto flex h-fit w-full max-w-lg flex-col p-4 md:flex-row md:px-6 md:py-8 lg:gap-20 lg:px-3 lg:py-10",
        leftContainer: "flex w-full flex-col gap-4 md:justify-center md:gap-6",
        rightContainer: "flex h-fit w-fit md:-ml-10 lg:ml-0",
        contentContainer: "flex h-fit flex-col gap-1 md:gap-2",
        heading:
          "font-montserrat text-heading-3 text-neutral-600 md:text-heading-2 lg:text-heading-1",
        subheading:
          "font-montserrat text-subheading-3-400 uppercase text-neutral-500 md:text-subheading-2",
        illustration:
          "hidden md:block md:aspect-[300/205] md:max-h-[205px] md:max-w-[300px] lg:aspect-[434/300] lg:max-h-[300px] lg:max-w-[434px]",
        image: "",
        ctaContainer: "flex flex-row gap-3 md:gap-4 lg:gap-6",
        primaryCta: "",
        secondaryCta: "",
      },
    },
    {
      type: "one-CTA",
      asset: "illustration",
      className: {
        root: "flex h-fit w-full flex-col bg-primary-100",
        container:
          "mx-auto flex h-fit w-full max-w-lg flex-col p-4 md:flex-row md:px-6 md:py-8 lg:gap-6 lg:px-3 lg:py-10",
        leftContainer: "flex w-full flex-col gap-4 md:justify-center md:gap-6",
        rightContainer: "flex h-fit w-fit md:-ml-10 lg:ml-0",
        contentContainer: "flex h-fit flex-col gap-1 md:gap-2",
        heading:
          "font-montserrat text-heading-3 text-neutral-600 md:text-heading-2 lg:text-heading-1",
        subheading:
          "font-montserrat text-subheading-3-400 text-neutral-500 md:text-subheading-2",
        illustration:
          "hidden md:block md:aspect-[300/205] md:max-h-[205px] md:max-w-[300px] lg:aspect-[434/300] lg:max-h-[300px] lg:max-w-[438px]",
        image: "",
        ctaContainer: "flex flex-row gap-3 md:gap-4 lg:gap-6",
        primaryCta: "flex w-fit items-center",
        secondaryCta: "",
      },
    },
    {
      type: "two-CTA",
      asset: "illustration",
      className: {
        root: "flex h-fit w-full flex-col bg-primary-100",
        container:
          "mx-auto flex h-fit w-full max-w-lg flex-col p-4 md:flex-row md:px-6 md:py-8 lg:gap-20 lg:px-3 lg:py-10",
        leftContainer: "flex w-full flex-col gap-4 md:justify-center md:gap-6",
        rightContainer: "flex h-fit w-fit md:-ml-10 lg:ml-0",
        contentContainer: "flex h-fit flex-col gap-1 md:gap-2",
        heading:
          "font-montserrat text-heading-3 text-neutral-600 md:text-heading-2 lg:text-heading-1",
        subheading:
          "font-montserrat text-subheading-3-400 text-neutral-500 md:text-subheading-2",
        illustration:
          "hidden md:block md:aspect-[300/205] md:max-h-[205px] md:max-w-[300px] lg:aspect-[434/300] lg:max-h-[300px] lg:max-w-[434px]",
        image: "",
        ctaContainer: "flex flex-row gap-3 md:gap-4 lg:gap-6",
        primaryCta: "w-full text-center sm:w-fit sm:text-start",
        secondaryCta: "w-full text-center sm:w-fit sm:text-start",
      },
    },
    {
      type: "one-CTA",
      asset: "image",
      className: {
        root: "flex h-fit w-full flex-col bg-white",
        container:
          "mx-auto grid h-fit w-full max-w-lg grid-cols-1 grid-rows-2 gap-4 p-4 md:grid-cols-2 md:grid-rows-1 md:px-6 md:py-8 lg:gap-6 lg:px-3 lg:py-10",
        leftContainer:
          "flex w-full flex-col gap-4 md:justify-center md:gap-6 lg:py-8",
        rightContainer: "relative order-last flex h-full w-full items-center",
        contentContainer: "flex h-fit flex-col gap-1 md:gap-2",
        heading:
          "font-montserrat text-heading-3 text-neutral-600 md:text-heading-2 lg:text-heading-1",
        subheading:
          "font-montserrat text-subheading-3-400 text-neutral-500 md:text-subheading-2",
        illustration:
          "hidden md:block md:aspect-[300/205] md:max-h-[205px] md:max-w-[300px] lg:aspect-[434/300] lg:max-h-[300px] lg:max-w-[434px]",
        image:
          "flex w-full rounded-3xl object-cover shadow-[4px_4px_0px_0px_#FFD5BF] md:h-full md:shadow-[6px_6px_0px_0px_#FFD5BF] lg:aspect-[620/376] lg:shadow-[8px_8px_0px_0px_#FFD5BF]",
        ctaContainer: "flex flex-row gap-3 md:gap-4 lg:gap-6",
        primaryCta: "w-fit",
        secondaryCta: "w-fit",
      },
    },
    {
      type: "two-CTA",
      asset: "image",
      className: {
        root: "flex h-fit w-full flex-col bg-white",
        container:
          "mx-auto grid h-fit w-full max-w-lg grid-cols-1 grid-rows-2 gap-4 p-4 md:grid-cols-2 md:grid-rows-1 md:px-6 md:py-8 lg:gap-6 lg:px-3 lg:py-10",
        leftContainer:
          "flex w-full flex-col gap-4 md:justify-center md:gap-6 lg:py-8",
        rightContainer: "relative order-last flex h-full w-full items-center",
        contentContainer: "flex h-fit flex-col gap-1 md:gap-2",
        heading:
          "font-montserrat text-heading-3 text-neutral-600 md:text-heading-2 lg:text-heading-1",
        subheading:
          "font-montserrat text-subheading-3-400 text-neutral-500 md:text-subheading-2",
        illustration:
          "hidden md:block md:aspect-[300/205] md:max-h-[205px] md:max-w-[300px] lg:aspect-[434/300] lg:max-h-[300px] lg:max-w-[434px]",
        image:
          "flex aspect-[358/180] h-full w-full rounded-3xl object-cover shadow-[4px_4px_0px_0px_#FFD5BF] md:aspect-[300/152] md:shadow-[6px_6px_0px_0px_#FFD5BF] lg:aspect-[620/376] lg:shadow-[8px_8px_0px_0px_#FFD5BF]",
        ctaContainer: "flex flex-row gap-3 md:gap-4 lg:gap-6",
        primaryCta: "w-fit",
        secondaryCta: "w-fit",
      },
    },
  ],
  defaultVariants: {
    type: "search",
    asset: "illustration",
  },
});

type BaseProps = {
  id?: string;
  heading?: string;
  subheading?: string;
  illustration?: React.FC<React.SVGProps<SVGSVGElement>>;
  image?: { src: string | StaticImageData; alt: string };
} & VariantProps<typeof headerBlogsEventsClassesVariants>;

type SearchProps = BaseProps & {
  type: "search";
  asset: "illustration";
  dropdownSearchBar?: DropdownSearchBarProps<unknown>;
};

type OneCTAProps = BaseProps & {
  type: "one-CTA";
  asset: "illustration" | "image";
  primaryCta: { text: string; href: string; icon?: React.ReactNode };
};

type TwoCTAProps = BaseProps & {
  type: "two-CTA";
  asset: "illustration" | "image";
  primaryCta: { text: string; href: string };
  secondaryCta: { text: string; href: string };
};

export type HeaderBlogsEventsClassesProps =
  | SearchProps
  | OneCTAProps
  | TwoCTAProps;

const HeaderBlogsEventsClasses: React.FC<HeaderBlogsEventsClassesProps> = (
  props
) => {
  const {
    id = "header-blogs-events-classes",
    type,
    asset,
    heading = "Heading",
    subheading = "Lorem ipsum dolor sit amet consectetur.",
    illustration,
    image,
  } = props;

  const Illustration = illustration || BlogsIllustration;
  const {
    root: rootStyle,
    container: containerStyle,
    leftContainer: leftContainerStyle,
    rightContainer: rightContainerStyle,
    contentContainer: contentContainerStyle,
    heading: headingStyle,
    subheading: subheadingStyle,
    illustration: illustrationStyle,
    image: imageStyle,
    ctaContainer: ctaContainerStyle,
    primaryCta: primaryCtaStyle,
    secondaryCta: secondaryCtaStyle,
  } = headerBlogsEventsClassesVariants({ type, asset });

  return (
    <section
      id={id}
      className={rootStyle()}
      role="region"
      aria-labelledby={`${id}-heading`}
    >
      <div className={containerStyle()}>
        <div className={leftContainerStyle()}>
          <div className={contentContainerStyle()}>
            <h1
              id={`${id}-heading`}
              className={headingStyle()}
            >
              {heading}
            </h1>
            <p className={subheadingStyle()}>{subheading}</p>
          </div>
          {type === "search" && (
            <div
              className="flex flex-col gap-4"
              role="search"
            >
              <DropdownSearchWrapper />
            </div>
          )}
          {type === "one-CTA" && (
            <div
              className={ctaContainerStyle()}
              role="navigation"
            >
              <PrimaryButton
                className={primaryCtaStyle()}
                href={props.primaryCta.href}
                size="md"
                disabled={!Boolean(props.primaryCta.href)}
                color="orange"
              >
                {props.primaryCta.icon}
                {props.primaryCta.text}
              </PrimaryButton>
            </div>
          )}
          {type === "two-CTA" && (
            <div
              className={ctaContainerStyle()}
              role="navigation"
            >
              <PrimaryButton
                className={primaryCtaStyle()}
                href={props.primaryCta.href}
                size="md"
                color="orange"
              >
                {props.primaryCta.text}
              </PrimaryButton>
              <SecondaryButton
                className={secondaryCtaStyle()}
                color="teal"
                size="md"
                transparent={true}
                href={props.secondaryCta.href}
              >
                {props.secondaryCta.text}
              </SecondaryButton>
            </div>
          )}
        </div>
        <div
          className={rightContainerStyle()}
          role="presentation"
        >
          {asset === "image" && (
            <Image
              className={imageStyle()}
              src={image?.src || CIVRStoriesBannerImage}
              alt={image?.alt || "Banner Image"}
              fill
              sizes="100vw"
              priority
            />
          )}
          {asset === "illustration" && (
            <Illustration className={illustrationStyle()} />
          )}
        </div>
      </div>
    </section>
  );
};

export default HeaderBlogsEventsClasses;
